import React from 'react'
import _ from 'lodash'
import httpStatusCodes from 'node-status-codes'
import * as styles from './styles'
import { OperationParameters, OperationResponses, Markdown, Schema, BackReferenceList } from '..'
import { operationToSlug } from '../../util'
import {
  Row,
  ContentColumn,
  CodeColumn,
  DividerHeader,
  TechnicalSubTitle,
} from '../../styles/utils'


export default ({ content, operationMetadata, openapi, operation }) => {
  const allParams = _.cloneDeep(openapi.getPath(operation, 'parameters')) || []

  if (_.has(operation, 'requestBody')) {
    const schema = openapi.getPath(operation.requestBody, ['content', 'application/json', 'schema'])
    const bodyParam = {
      __body: true,
      name: 'body',
      required: operation.requestBody.required,
      schema,
      description: operation.requestBody.description || schema.description || 'The request body',
    }

    allParams.push(bodyParam)
  }

  const responses = openapi.getPath(operation, 'responses')
  const responsesArr = _.map(responses, (response, statusCodeStr) => {
    // Transform object to array
    // { 200: { description: "..." }} -> [{ statusCode: 200, description: '...}]
    const statusCode = parseInt(statusCodeStr, 10)
    return _.merge({}, response, {
      statusCode,
      statusText: `${statusCode} ${httpStatusCodes[statusCode]}`,
    })
  })

  const successfulResponses = _.map(
    _.filter(responsesArr, r => r.statusCode < 400),
    (response, i) => {
      const content = openapi.getPath(response, 'content')
      const responseSchema = _.has(content, 'application/json')
        ? openapi.getPath(content, ['application/json', 'schema'])
        : openapi.getPath(content, 'schema')

      if (!responseSchema) {
        console.warn(`Couldn't find a response schema for operation: ${operationMetadata.id}`)
      }

      return _.merge({}, response, {
        responseSchema
      })
    }
  )

  const referenceSection = (
    <React.Fragment>
      {
        allParams.length > 0
          ? (
            <OperationParameters
              operation={operation}
              operationMetadata={operationMetadata}
              openapi={openapi}
              parameters={allParams}
            />
          )
          : null
      }

      <OperationResponses
        operation={operation}
        operationMetadata={operationMetadata}
        openapi={openapi}
        responses={successfulResponses}
      />

      {
        allParams.length > 0 && _.last(allParams).__body
          ? (
            <React.Fragment>
              <DividerHeader id={`${operationMetadata.id}-body`}>Request format</DividerHeader>
              <Schema openapi={openapi} content={content} schema={_.last(allParams).schema} />
            </React.Fragment>
          )
          : null
      }

      {
        _.map(successfulResponses, (response, i) => {
          const schema = !response.responseSchema
            ? <p>Response schema unknown.</p>
            : <Schema openapi={openapi} content={content} schema={response.responseSchema} />

          return (
            <React.Fragment key={i}>
              <DividerHeader id={`${operationMetadata.id}-${response.statusCode}-response`}>
                Response format
              </DividerHeader>
              {schema}
            </React.Fragment>
          )
        })
      }
    </React.Fragment>
  )

  const examplesSection = (
    <React.Fragment>
      {
        // It's useful to have the example generation in client-side
        // This way we can in the future take user's example API keys from their session
        _.map(operationMetadata.examples, (example, i) => {
          const data = {
            requestSnippetLanguage: 'bash',
            requestSnippetCode: example._requestSnippets.curl,
            responseJson: example._response.body,
            beforeRequest: _.get(example, 'markdownContents.beforeRequest', ''),
            afterRequest: _.get(example, 'markdownContents.afterRequest', ''),
            beforeResponse: _.get(example, 'markdownContents.beforeResponse', ''),
            afterResponse: _.get(example, 'markdownContents.afterResponse', ''),
          }

          const mdContent = content.templates.requestExample(data)
          return <styles.Example key={i}><Markdown>{mdContent}</Markdown></styles.Example>
        })
      }
    </React.Fragment>
  )

  return (
    <styles.Container id={operationToSlug(operationMetadata)}>
      <Row>
        <ContentColumn>
          <h2>{operationMetadata.title}</h2>
          <TechnicalSubTitle>{operationMetadata.method.toUpperCase()} {operationMetadata.path}</TechnicalSubTitle>
        </ContentColumn>
        <CodeColumn />
      </Row>

      {_.map(operationMetadata.markdownLayout, (block, i) => {
        const isLast = i === operationMetadata.markdownLayout.length - 1
        return (
          <Row key={i}>
            <ContentColumn>
              <Markdown shiftHeadingLevel={2} ast={block.contentCol} />

              { isLast ? referenceSection : null }
            </ContentColumn>
            <CodeColumn>
              <Markdown shiftHeadingLevel={2} ast={block.codeCol} />

              { isLast ? examplesSection : null }
            </CodeColumn>
          </Row>
        )
      })}
      <Row>
        <BackReferenceList content={content} refName={operationMetadata.title} />
      </Row>
    </styles.Container>
  )
}
